import type { TranslationDictionary } from "@common/i18n";

export type LanguageTag = "nb" | "en";

export type TranslationNamespace =
  | "addressSearch"
  | "anonymousTransaction"
  | "applications"
  | "audits"
  | "bookings"
  | "businessInsights"
  | "camerasOverview"
  | "common"
  | "countries"
  | "coupons"
  | "enums"
  | "error"
  | "faq"
  | "faqCategories"
  | "fingerprint"
  | "footer"
  | "gdpr"
  | "home"
  | "invitations"
  | "legal"
  | "manualApproval"
  | "manualApprovalReview"
  | "map"
  | "organizations"
  | "payment"
  | "paymentSources"
  | "priceCategoryType"
  | "priceMatrix"
  | "priceMatrixAdmin"
  | "profile"
  | "receipts"
  | "remit"
  | "signIn"
  | "statistics"
  | "subscriptions"
  | "system"
  | "support"
  | "telemetry"
  | "tour"
  | "trafficControl"
  | "users"
  | "vehicleRegistry"
  | "vehicles"
  | "youpark"
  | "youparkPlus"
  | "youparkReports"
  | "youparkTodos";

export const baseTranslationNamespaces: TranslationNamespace[] = [
  "common",
  "countries",
  "enums",
  "priceCategoryType",
  "footer",
  "support",
  "error"
];

export const supportedLanguages: LanguageTag[] = ["nb", "en"];

const baseCache = new Map<LanguageTag, TranslationDictionary>();
const baseLoading = new Map<LanguageTag, Promise<TranslationDictionary>>();
const namespaceCache = new Map<LanguageTag, Map<TranslationNamespace, TranslationDictionary>>();
const namespaceLoading = new Map<string, Promise<TranslationDictionary>>();

const namespaceCacheKey = (lang: LanguageTag, namespace: TranslationNamespace) => `${lang}:${namespace}`;

const resolveModule = (module: Record<string, unknown>, namespace?: TranslationNamespace): TranslationDictionary => {
  if (namespace && namespace in module && typeof module[namespace] === "object") {
    return module[namespace] as TranslationDictionary;
  }
  if (module.default && typeof module.default === "object") {
    return module.default as TranslationDictionary;
  }
  throw new Error(`Missing translations for namespace ${namespace ?? "base"}`);
};

export async function loadLanguage(lang: LanguageTag): Promise<TranslationDictionary> {
  const cached = baseCache.get(lang);
  if (cached) {
    return cached;
  }
  const pending = baseLoading.get(lang);
  if (pending) {
    return pending;
  }
  const loadPromise = import(`./${lang}/base`)
    .then(module => {
      const translations = resolveModule(module as Record<string, unknown>);
      baseCache.set(lang, translations);
      baseLoading.delete(lang);
      return translations;
    })
    .catch(error => {
      baseLoading.delete(lang);
      throw error;
    });

  baseLoading.set(lang, loadPromise);
  return loadPromise;
}

const addNamespaceRoot = (
  dictionary: TranslationDictionary,
  namespace?: TranslationNamespace
): TranslationDictionary => {
  if (!namespace) {
    return dictionary;
  }
  if (typeof dictionary === "object" && !Array.isArray(dictionary) && namespace in dictionary) {
    return dictionary;
  }
  return { [namespace]: dictionary };
};

const loadNamespace = async (lang: LanguageTag, namespace: TranslationNamespace) => {
  const cache = namespaceCache.get(lang) ?? new Map<TranslationNamespace, TranslationDictionary>();
  namespaceCache.set(lang, cache);
  const cached = cache.get(namespace);
  if (cached) {
    return cached;
  }
  const key = namespaceCacheKey(lang, namespace);
  const pending = namespaceLoading.get(key);
  if (pending) {
    return pending;
  }
  const loadPromise = import(`./${lang}/${namespace}`)
    .then(module => {
      const translations = resolveModule(module as Record<string, unknown>, namespace);
      const namespacedTranslations = addNamespaceRoot(translations, namespace);
      cache.set(namespace, namespacedTranslations);
      namespaceLoading.delete(key);
      return namespacedTranslations;
    })
    .catch(error => {
      namespaceLoading.delete(key);
      throw error;
    });

  namespaceLoading.set(key, loadPromise);
  return loadPromise;
};

export async function loadLanguageNamespaces(
  lang: LanguageTag,
  namespaces: TranslationNamespace[]
): Promise<TranslationDictionary[]> {
  const uniqueNamespaces = Array.from(new Set(namespaces));
  return Promise.all(uniqueNamespaces.map(namespace => loadNamespace(lang, namespace)));
}

export function resolveNamespacesForRoute(routeName?: string | null): TranslationNamespace[] {
  if (!routeName) {
    return [];
  }

  if (routeName === "sign-in") {
    return ["signIn"];
  }

  if (routeName === "verify-fingerprint") {
    return ["fingerprint"];
  }

  if (routeName === "home") {
    return ["home"];
  }

  if (routeName.startsWith("map")) {
    return ["map", "subscriptions", "paymentSources", "vehicles", "anonymousTransaction", "priceMatrixAdmin"];
  }

  if (routeName.startsWith("faq") || routeName === "customer-service") {
    return ["faq", "payment"];
  }

  if (routeName === "gdpr") {
    return ["gdpr"];
  }

  if (routeName.includes("anonymoustransaction")) {
    return ["anonymousTransaction"];
  }

  if (routeName === "tour") {
    return ["tour"];
  }

  if (
    routeName === "cookies" ||
    routeName === "privacypolicy" ||
    routeName === "terms" ||
    routeName === "dataprocessors"
  ) {
    return ["legal"];
  }

  if (
    routeName === "youpark-plus" ||
    routeName === "youpark-plus-terms" ||
    routeName === "youpark-plus-start" ||
    routeName === "youpark-plus-status"
  ) {
    return ["youparkPlus"];
  }

  if (routeName.startsWith("profile")) {
    return ["profile"];
  }

  if (routeName.startsWith("payment-sources")) {
    return ["paymentSources"];
  }

  if (routeName.startsWith("payment")) {
    return ["payment"];
  }

  if (routeName.startsWith("vehicles") || routeName.startsWith("add-vehicle") || routeName.startsWith("edit-vehicle")) {
    return ["vehicles", "paymentSources"];
  }

  if (routeName === "subscriptions" || routeName.startsWith("subscription-")) {
    return ["subscriptions", "paymentSources", "vehicles"];
  }

  if (routeName.includes("invitation")) {
    return ["subscriptions", "invitations"];
  }

  if (routeName === "coupons") {
    return ["coupons"];
  }

  if (routeName === "bookings") {
    return ["bookings"];
  }

  if (routeName === "receipts") {
    return ["receipts"];
  }

  if (routeName.startsWith("remit")) {
    return ["remit"];
  }

  if (routeName.startsWith("youpark")) {
    const namespaces: TranslationNamespace[] = ["youpark"];
    if (routeName.startsWith("youpark-booking-reservations")) {
      namespaces.push("bookings");
    }
    if (routeName.startsWith("youpark-subscriptions-")) {
      namespaces.push("priceMatrix");
    }
    if (routeName.startsWith("youpark-customer-admin")) {
      namespaces.push("priceMatrixAdmin");
    }
    if (routeName.startsWith("youpark-customer-register")) {
      namespaces.push("subscriptions");
    }
    if (routeName.includes("insights")) {
      namespaces.push("businessInsights");
    }
    return namespaces;
  }

  if (routeName === "cameras-overview") {
    return ["camerasOverview"];
  }

  if (routeName.startsWith("applications")) {
    return ["applications"];
  }

  if (routeName.startsWith("users") || routeName.startsWith("user-")) {
    return ["users"];
  }

  if (routeName.startsWith("organizations")) {
    return ["organizations", "users"];
  }

  if (routeName === "manual-approval") {
    return ["manualApproval"];
  }

  if (routeName === "manual-approval-review") {
    return ["manualApprovalReview"];
  }

  if (routeName === "traffic-control") {
    return ["trafficControl"];
  }

  if (routeName === "system") {
    return ["system"];
  }

  if (routeName === "audits") {
    return ["audits"];
  }

  if (routeName.startsWith("statistics")) {
    return ["statistics"];
  }

  if (routeName.startsWith("telemetry")) {
    return ["telemetry"];
  }

  if (routeName === "vehicle-registry") {
    return ["vehicleRegistry"];
  }

  if (routeName.startsWith("error-")) {
    return ["error"];
  }

  return [];
}
